import { Bot, PlusCircle } from 'lucide-react'

import { UserAvatar } from '~/components/UserAvatar'
import { Timestamp } from '~/components/component-library/Timestamp'
import { UserDisplay } from '~/components/component-library/UserDisplay'
import { UserPopover } from '~/components/component-library/UserDisplay/components/UserPopover'
import { TransactionTimelineRowProps } from '~/components/txn-display/timeline/components/TimelineRow'
import { useActiveOrg } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import {
  BackendTransaction,
  SmartAccountRole,
  TransactionStatus,
} from '~/types'

export function useCreatedByRow({
  transaction,
  smartAccountRole,
}: {
  transaction: BackendTransaction
  smartAccountRole?: SmartAccountRole
}): TransactionTimelineRowProps | null {
  const { org } = useActiveOrg()
  const { user } = useAuthUser()

  const createdByUserId =
    'createdByUserId' in transaction ? transaction.createdByUserId : undefined

  const creator = org?.members.find(({ id }) => id === createdByUserId) ?? null

  if (!creator) {
    const isAutomation = smartAccountRole === SmartAccountRole.AUTOMATION

    return {
      key: `created-${transaction.id}`,
      icon: isAutomation ? (
        <Bot size={16} className="text-secondary" />
      ) : (
        <PlusCircle size={16} className="text-secondary" />
      ),
      content: isAutomation ? (
        <span className="flex min-w-0 items-center gap-1">
          <span className="flex-shrink-0 whitespace-nowrap">Created by</span>
          <span className="min-w-0 truncate font-medium text-primary">bot</span>
        </span>
      ) : (
        <span className="flex-shrink-0 whitespace-nowrap">Created</span>
      ),
      meta: (
        <Timestamp
          timestamp={transaction.createdAt}
          type="relative"
          abbreviate={true}
        />
      ),
    }
  }

  const isUser = creator.id === user?.id

  const label =
    transaction.status === TransactionStatus.Drafted
      ? 'Drafted by'
      : 'Created by'

  return {
    key: `created-${transaction.id}`,
    icon: <UserAvatar user={creator} size={16} />,
    content: (
      <UserPopover user={creator} align="start">
        <span className="flex min-w-0 items-center gap-1">
          <span className="flex-shrink-0 whitespace-nowrap">{label}</span>
          {creator.displayName ? (
            <span className="min-w-0 truncate font-medium text-primary">
              {isUser ? (
                'you'
              ) : (
                <UserDisplay user={creator} layout="inline-text" hidePopover />
              )}
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate font-medium text-primary">
              {isUser ? 'you' : creator.email}
            </span>
          )}
        </span>
      </UserPopover>
    ),
    meta: (
      <Timestamp
        timestamp={transaction.createdAt}
        type="relative"
        abbreviate={true}
      />
    ),
  }
}
